"use client";

import React from "react";
import Hero from "@/components/tools/Hero";
import FAQ from "@/components/tools/FAQ";
import RelatedTopics from "@/components/tools/RelatedTopics";
import WhatIsFluoride from "@/components/services/fluoride-treatment/WhatIsFluoride";
import WhyFluorideIsEssential from "@/components/services/fluoride-treatment/WhyFluorideIsEssential";
import ApplyingFluoride from "@/components/services/fluoride-treatment/ApplyingFluoride";
import DentalProfessionals from "@/components/services/fluoride-treatment/DentalProfessionals";
import ComprehensiveDentalHealth from "@/components/services/fluoride-treatment/ComprehensiveDentalHealth";
import PreventiveMeasures from "@/components/services/fluoride-treatment/PreventiveMeasures";
import UnderstandingDecay from "@/components/services/fluoride-treatment/UnderstandingDecay";
import FluorideIntro from "@/components/services/fluoride-treatment/FluorideIntro";

const fluorideFAQs = [
  {
    question: "What is fluoride and why is it important?",
    answer:
      "<p>Fluoride is a natural mineral found in soil, water, and even in small amounts in our food. It's crucial for oral health as it helps prevent tooth decay by making teeth more resistant to acids produced by harmful bacteria in your mouth.</p><p>Our dental team at Smile4Me can provide professional fluoride treatments during routine check-ups, ensuring you receive a concentrated dose of this essential mineral and an extra layer of protection for your teeth.</p>",
  },
  {
    question: "How does fluoride treatment work?",
    answer:
      "<p>Fluoride treatments are quick and painless. During the process, we apply a concentrated form of fluoride directly to your teeth using one of three methods: foam, gel, or varnish. The fluoride becomes part of the tooth's structure, providing ongoing protection against decay.</p><p>The treatment only takes a few minutes, and you can continue with your day afterward. However, it's important not to eat or drink for about half an hour to allow the fluoride to be fully absorbed by your teeth.</p>",
  },
  {
    question: "Who needs fluoride treatments?",
    answer:
      "<p>Fluoride treatments are beneficial for everyone, but they're particularly important for children as their permanent teeth begin to erupt. Strong tooth enamel during early development can help prevent decay and cavities throughout their lives. Adults can also benefit from fluoride treatments, especially if they're at a higher risk for developing tooth decay due to factors like dry mouth, gum disease, or a history of frequent cavities.</p><p>At Smile4Me, we recommend regular fluoride treatments as part of our preventive dentistry program to ensure optimal oral health for patients of all ages.</p>",
  },
  {
    question:
      "What are the different forms and processes of applying fluoride?",
    answer:
      "<p>Fluoride treatments can be applied in various forms during your routine dental check-ups. The most common methods include fluoride foam, gel, or varnish. Each form has its unique advantages but shares the same goal – to provide a concentrated dose of fluoride directly to your teeth.</p><p>Our team at Smile4Me will determine the best method for you based on your individual needs and preferences.</p>",
  },
  {
    question: "How can dental professionals help with fluoride treatment?",
    answer:
      "<p>Dental professionals, like our team at Smile4Me, are your best source of information and care when it comes to fluoride treatments. We have the training, experience, and knowledge to provide you with personalized recommendations based on your unique oral health needs.</p><p>During your routine dental check-ups, we'll assess your risk for tooth decay and determine if a fluoride treatment is appropriate. If so, we'll apply it quickly and painlessly using one of the methods mentioned earlier.</p>",
  },
  {
    question: "What role does fluoride play in combating tooth decay?",
    answer:
      "<p>Tooth decay is a progressive disease caused by harmful bacteria that feed on sugars from the foods and drinks we consume. These bacteria produce acids that attack the enamel layer of our teeth, leading to demineralization – the loss of minerals over time.</p><p>Fluoride plays a critical role in combating this decay by promoting remineralization – the process where minerals are redeposited onto the enamel layer of our teeth. This makes them stronger and more resistant to acid attacks, helping to reverse early signs of decay and prevent cavities.</p>",
  },
  {
    question:
      "What other preventive measures can help maintain good oral health?",
    answer:
      "<p>While fluoride treatments are crucial for preventing tooth decay, they're just one piece of the puzzle. Regular brushing and flossing are essential for removing plaque and food debris that can lead to cavities if left untreated.</p><p>In addition to fluoride treatments, our dental team at Smile4Me may recommend other preventive measures tailored to your individual needs. These could include using a fluoride mouthwash at home, applying dental sealants to the chewing surfaces of your back teeth, or attending regular dental check-ups for early detection and treatment of any issues.</p>",
  },
  {
    question: "What happens during a routine dental check-up?",
    answer:
      "<p>A routine dental check-up at Smile4Me involves several steps designed to maintain optimal oral health. Our team will begin by cleaning your teeth, removing plaque and tartar buildup that regular brushing and flossing may have missed.</p><p>After the cleaning, we'll examine your teeth, gums, and mouth for any signs of decay, gum disease, or other issues. We may also take X-rays to help us detect problems not visible to the naked eye. If necessary, we'll provide a fluoride treatment as part of our preventive dentistry program.</p>",
  },
  {
    question:
      "How can I maintain my teeth's natural strength and protect against decay?",
    answer:
      "<p>Maintaining your teeth's natural strength involves a combination of good oral hygiene habits at home and regular professional care from our team at Smile4Me. Here are some tips to help protect against decay:</p><p>1. Brush twice a day with a fluoride toothpaste, ensuring you reach all surfaces of your teeth.<br>2. Floss daily to remove plaque and food debris from between your teeth and along the gumline.<br>3. Limit your intake of sugary foods and drinks, as they feed harmful bacteria in your mouth.<br>4. Attend regular dental check-ups and cleanings at our office, where we can provide professional fluoride treatments and monitor your oral health.</p>",
  },
  {
    question: "What should I do if I think I have a cavity?",
    answer:
      "<p>If you suspect that you may have a cavity, it's essential to schedule an appointment with our dental team at Smile4Me as soon as possible. Early detection and treatment of cavities can help prevent them from becoming larger and more costly to repair.</p><p>In the meantime, avoid consuming acidic or sugary foods and drinks, which can exacerbate the problem. If you experience any pain or sensitivity, over-the-counter pain relievers may provide temporary relief until your appointment.</p>",
  },
];

const FluorideTreatment: React.FC = () => {
  return (
    <>
      <Hero
        imageSrc="/images/system_images_services_dental_banners_fluoride_treatment.webp"
        imageAlt="Fluoride Treatments Banner"
        title="Fluoride Treatment"
        subtitle="Strengthen your teeth with expert fluoride treatments at Smile4Me Dental Care."
        ctaText="Book Now"
        ctaLink="/contact"
      />
      <main className="flex-1 py-8 bg-gradient-to-b from-white to-gray-50">
        <div className="flex flex-col lg:flex-row mx-auto px-4 sm:px-8 md:px-12">
          <article className="flex-1 space-y-8 pr-0 lg:pr-8">
            <FluorideIntro />
            <WhatIsFluoride />
            <WhyFluorideIsEssential />
            <ApplyingFluoride />
            <DentalProfessionals />
            <ComprehensiveDentalHealth />
            <PreventiveMeasures />
            <UnderstandingDecay />
            <FAQ title="Frequently Asked Questions" faqs={fluorideFAQs} />
          </article>
          <RelatedTopics
            topics={[
              {
                label: "Dental Cleanings",
                url: "/services/dental-cleanings",
              },
              { label: "Dental Sealants", url: "/services/dental-sealants" },
              { label: "Teeth Whitening", url: "/services/teeth-whitening" },
              { label: "Oral Exams", url: "/services/oral-exams" },
            ]}
          />
        </div>
      </main>
    </>
  );
};

export default FluorideTreatment;